import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CanDeleteUserValidation, User } from '../../../api/app-types/user.app';
import { USER_SERVICE, UserApi } from '../../../api/services/user.service';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-user-delete-dialog',
	template: `
		<h2 mat-dialog-title>{{ 'user.delete.title' | transloco }}</h2>
		@if (canDeleteUser() === false) {
			<mat-dialog-content>
				@if (shouldShowRemainingAssignment()) {
					<p>
						{{ 'user.delete.remainingAssignment' | transloco: { firstName: user.firstName, lastName: user.lastName } }}
					</p>
				}
				@if (shouldShowPositionShareExitRequired()) {
					<p>
						{{ 'user.delete.position_share_exit_required' | transloco }}
					</p>
				}
			</mat-dialog-content>
			<mat-dialog-actions class="actions-row">
				<lvr-button
					variant="primary"
					[label]="'user.delete.ok' | transloco"
					[type]="'button'"
					mat-dialog-close
					icon="check"
				></lvr-button>
			</mat-dialog-actions>
		} @else {
			<mat-dialog-content>
				<p>
					{{ 'user.delete.confirmation' | transloco: { firstName: user.firstName, lastName: user.lastName } }}
				</p>
				<p>{{ 'user.delete.no_revert' | transloco }}</p>
				<lvr-field [label]="'user.delete.doublecheck' | transloco">
					<mat-form-field lvrInput class="full-width">
						<input
							matInput
							[(ngModel)]="confirmationInput"
							[placeholder]="'user.delete.insert_name' | transloco: { lastName: user.lastName }"
						/>
					</mat-form-field>
				</lvr-field>
			</mat-dialog-content>
			<mat-dialog-actions class="actions-row">
				<lvr-button
					variant="secondary"
					[label]="'user.delete.cancel' | transloco"
					[disabled]="loading()"
					[type]="'button'"
					icon="disabled_by_default"
					mat-dialog-close
				></lvr-button>
				<lvr-button
					variant="primary"
					[label]="'user.delete.delete' | transloco"
					[type]="'button'"
					[disabled]="!isDeleteEnabled() || loading()"
					[loading]="loading()"
					[loadingLabel]="'user.delete.deleting' | transloco"
					(clicked)="deleteUser()"
					icon="delete"
				></lvr-button>
			</mat-dialog-actions>
		}
	`,
	styles: [
		`
			.full-width {
				width: 100%;
				margin-top: 16px;
			}
		`,
	],
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatDialogClose,
		MatFormField,
		MatInput,
		FormsModule,
		LvrInputDirective,
		LvrField,
		TranslocoPipe,
		LvrButton,
	],
	providers: [{ provide: USER_SERVICE, useClass: UserApi }],
})
export class UserDeleteDialog implements OnInit {
	public readonly user: User = inject(MAT_DIALOG_DATA);
	private readonly dialogRef = inject(MatDialogRef<UserDeleteDialog>);
	private readonly userService = inject(USER_SERVICE);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);

	public readonly confirmationInput = signal('');
	public readonly loading = signal(false);
	public readonly deleteValidation = signal<CanDeleteUserValidation | null>(null);
	public readonly canDeleteUser = computed(() => this.deleteValidation()?.canDelete === true);

	public readonly isDeleteEnabled = computed(() => {
		return this.confirmationInput().trim() === this.user.lastName.trim();
	});

	public ngOnInit() {
		this.checkCanDeleteUser();
	}

	public async checkCanDeleteUser() {
		const result = await this.userService.getCanDeleteUser(this.user.id);
		this.deleteValidation.set(result);
		return result;
	}

	public async deleteUser() {
		if (this.loading() || !this.isDeleteEnabled()) return;

		this.loading.set(true);

		try {
			const validation = await this.checkCanDeleteUser();
			if (!validation.canDelete) return;

			await this.userService.deleteUser(this.user.objectId);

			this.ntfySrv.addNotification(
				createSuccessNotification(
					this.transloco.translate('base.notification_message.user_delete_success', {
						firstName: this.user.firstName,
						lastName: this.user.lastName,
					}),
				),
			);
			this.dialogRef.close(true);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.user_delete_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}

	public shouldShowRemainingAssignment(): boolean {
		return this.deleteValidation()?.hasClients === true;
	}

	public shouldShowPositionShareExitRequired(): boolean {
		return this.deleteValidation()?.hasEndDate === false;
	}
}
